import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ListChecks, Loader2 } from 'lucide-react'
import { useBot } from '@/shared/hooks/useBots'
import { useResources } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useUser } from '@/shared/hooks/useUser'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { botsApi } from '@/shared/api/bots'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import type { Resource } from '@/shared/types'

export default function BotWhitelistAdd() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading, telegramId } = useTelegram()
  const { data: currentUser, isLoading: userLoading } = useUser(telegramId)
  const { data: bot, isLoading: botLoading } = useBot(botId)
  // Fetch current user's own resources (for adding to whitelist)
  const { data: myResources, isLoading: resourcesLoading } = useResources(currentUser?.id)

  // Fetch whitelisted resource IDs separately
  const { data: whitelistData, isLoading: whitelistLoading } = useQuery({
    queryKey: ['bot-whitelisted-resources', botId],
    queryFn: () => botsApi.getWhitelistedResources(botId!),
    enabled: !!botId,
  })

  const [selectedResources, setSelectedResources] = useState<string[]>([])
  const [error, setError] = useState<string>('')
  const [selectedType, setSelectedType] = useState<'all' | Resource['resource_type']>('all')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize selected resources from whitelisted IDs
  useEffect(() => {
    if (whitelistData?.resource_ids) {
      setSelectedResources(whitelistData.resource_ids)
    }
  }, [whitelistData])

  // Merge whitelisted resources + current user's own resources (deduplicated)
  const allAvailableResources = useMemo(() => {
    const resourceMap = new Map<string, Resource>()

    // Add already whitelisted resources (from any owner)
    if (whitelistData?.resources) {
      whitelistData.resources.forEach(r => resourceMap.set(r.id, r))
    }

    // Add current user's own resources
    if (myResources) {
      myResources.forEach(r => resourceMap.set(r.id, r))
    }

    return Array.from(resourceMap.values())
  }, [whitelistData?.resources, myResources])

  // Filtered resources based on type
  const filteredResources = useMemo(() => {
    let filtered = allAvailableResources

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(r => r.resource_type === selectedType)
    }

    return filtered
  }, [allAvailableResources, selectedType])

  const toggleResource = (resourceId: string) => {
    hapticFeedback('light')
    setSelectedResources((prev) =>
      prev.includes(resourceId)
        ? prev.filter((id) => id !== resourceId)
        : [...prev, resourceId]
    )
  }

  const selectAll = () => {
    hapticFeedback('medium')
    setSelectedResources(filteredResources.map((r) => r.id))
  }

  const clearAll = () => {
    hapticFeedback('medium')
    setSelectedResources([])
  }

  const selectedCount = selectedResources.length

  // Mutation for saving whitelist
  const saveWhitelistMutation = useMutation({
    mutationFn: async (resourceIds: string[]) => {
      if (!botId) throw new Error('Bot ID is required')
      await botsApi.setWhitelistedResources(botId, resourceIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot-whitelisted-resources', botId] })
      queryClient.invalidateQueries({ queryKey: ['bot', botId] })
      queryClient.invalidateQueries({ queryKey: ['bots'] })
      // Invalidate resource-side queries for bidirectional sync
      queryClient.invalidateQueries({ queryKey: ['resource-whitelisted-bots'] })
      queryClient.invalidateQueries({ queryKey: ['resources'] })
    },
  })

  const handleSave = async () => {
    if (!bot) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await saveWhitelistMutation.mutateAsync(selectedResources)
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      window.scrollTo(0, 0)
      navigate(`/bots/${botId}/whitelist`, {
        replace: true,
        state: { successMessage: 'Ресурсы успешно добавлены в белый список' }
      })
    } catch (error) {
      console.error('Failed to save whitelist:', error)
      setError('Ошибка при сохранении ресурсов')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  // Auto-manage back button
  useBackButton()

  // Setup MainButton
  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Добавить выбранные', onSave, '#5288c1', '#ffffff')

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, setMainButtonLoading])

  // Helper to get logo URL
  const getLogoUrl = (resource: Resource) => {
    if (!resource.id) return null

    const baseUrl = import.meta.env.VITE_OPSERVICE_BASE_URL || 'http://localhost:8080'
    return `${baseUrl}/api/v1/resources/${resource.id}/logo`
  }

  // Resource type metadata
  const typeMetadata: Record<Resource['resource_type'], { icon: string }> = {
    channel: { icon: '📢' },
    bot: { icon: '🤖' },
    group: { icon: '👥' },
    boost: { icon: '🚀' }
  }

  if (botLoading || userLoading || resourcesLoading || whitelistLoading || !bot) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
              <ListChecks className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-tg-text">Добавить ресурсы</h1>
              <p className="text-sm text-tg-hint mt-1">{bot.name}</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-tg-text">{selectedCount}</div>
              <div className="text-xs text-tg-hint">выбрано</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
          <p className="text-sm text-blue-400">
            Выберите свои ресурсы для продвижения через этого бота. Оплата не требуется.
          </p>
        </div>

        {allAvailableResources.length === 0 ? (
          <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-8 text-center">
            <p className="text-tg-hint">Нет доступных ресурсов для добавления</p>
            <button
              onClick={() => {
                hapticFeedback('light')
                navigate('/traffic')
              }}
              className="mt-4 px-6 py-2 bg-tg-button text-white rounded-xl"
            >
              Создать ресурс
            </button>
          </div>
        ) : (
          <>
            {/* Type Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['all', 'channel', 'bot', 'boost'].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    hapticFeedback('light')
                    setSelectedType(type as typeof selectedType)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    selectedType === type
                      ? 'bg-tg-button text-white shadow-md'
                      : 'bg-tg-secondary-bg text-tg-text hover:bg-tg-secondary-bg/80'
                  }`}
                >
                  {type === 'all' && 'Все'}
                  {type === 'channel' && 'Каналы'}
                  {type === 'bot' && 'Боты'}
                  {type === 'boost' && 'Бусты'}
                </button>
              ))}
            </div>

            {/* Batch Actions */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  selectAll()
                }}
                disabled={filteredResources.length === 0}
                className="flex-1 py-2 px-3 bg-[#5288c1]/30 text-[#5288c1] rounded-lg text-sm font-medium hover:bg-[#5288c1]/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Выбрать все ({filteredResources.length})
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  clearAll()
                }}
                disabled={selectedCount === 0}
                className="flex-1 py-2 px-3 bg-red-500/20 text-red-600 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Очистить ({selectedCount})
              </button>
            </div>

            {/* Resources List */}
            {filteredResources.length === 0 ? (
              <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-8 text-center">
                <p className="text-tg-hint">
                  {selectedType !== 'all'
                    ? 'У вас пока нет ресурсов этого типа'
                    : 'У вас пока нет ресурсов'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredResources.map((resource) => {
                  const isSelected = selectedResources.includes(resource.id)
                  const logoUrl = getLogoUrl(resource)
                  const typeIcon = typeMetadata[resource.resource_type]?.icon || '📦'

                  return (
                    <button
                      key={resource.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        toggleResource(resource.id)
                      }}
                      className={`w-full relative bg-tg-secondary-bg rounded-2xl p-4 shadow-sm transition-all ${
                        isSelected
                          ? 'ring-2 ring-tg-button bg-tg-button/5'
                          : 'hover:shadow-md active:scale-[0.98]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Resource Logo or Icon */}
                        <div className={`relative flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center ${
                          logoUrl ? 'bg-tg-bg' : 'bg-tg-hint/10'
                        }`}>
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt={resource.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                target.style.display = 'none'
                                const parent = target.parentElement
                                if (parent) {
                                  parent.innerHTML = `<div class="text-2xl">${typeIcon}</div>`
                                }
                              }}
                            />
                          ) : (
                            <div className="text-2xl">{typeIcon}</div>
                          )}
                        </div>

                        {/* Resource Info */}
                        <div className="flex-1 min-w-0 text-left">
                          <h3 className="text-base font-semibold text-tg-text truncate mb-0.5">
                            {resource.name}
                          </h3>
                          <p className="text-sm text-tg-hint truncate">
                            {resource.username ? `@${resource.username}` : resource.telegram_id || 'ID не указан'}
                          </p>
                        </div>

                        {/* Selection Indicator */}
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected
                            ? 'bg-tg-button border-tg-button'
                            : 'border-tg-hint/30'
                        }`}>
                          {isSelected && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
